import React, { useEffect, useState } from 'react';

import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { getTranslate } from 'react-localize-redux';

import { Button } from '../../../components/atoms/Button';
import { TextField } from '../../../components/atoms/TextField';
import { Typography } from '../../../components/atoms/Typography';
import { Add as AddIcon } from '../../../components/icons/Add';
import { Dialog } from '../../../components/layouts/Dialog';
import { domUtils } from '../../../utils/domUtils';
import { trackVoucherAdded } from '../../../utils/voucherUtils';
import {
  CollectionOrder,
  DeliveryOrder,
  MenuReposnse,
  VoucherBasketItemDm,
} from '../../../weborder/Api';
import { OrderPayload } from '../../../weborder/selectors/basketSelectors';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    paddingBottom: theme.spacing(2.5),
  },
}));

type VoucherProps = {
  code: string;
  status: string;
};

export type Props = {
  checkUserVoucher: (voucher: {
    code: string;
    orderPayload: DeliveryOrder | CollectionOrder;
  }) => any;
  handleClose: () => void;
  menu: MenuReposnse;
  orderPayload: OrderPayload;
  showVoucherDialog: boolean;
  translate: ReturnType<typeof getTranslate>;
  voucher: VoucherProps;
};

export function VoucherDialog(props: Props): JSX.Element {
  const {
    checkUserVoucher,
    handleClose,
    menu,
    orderPayload,
    showVoucherDialog,
    translate,
    voucher,
  } = props;

  const classes = useStyles();

  const { enqueueSnackbar } = useSnackbar();

  const [error, setError] = useState('');
  const [helperMessage, setHelperMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [userInput, setUserInput] = useState('');

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    setError('');
  }, [showVoucherDialog]);

  useEffect(() => {
    if (showVoucherDialog) {
      if (voucher.status === 'none') {
        setHelperMessage('');
        setModalTitle(translate('Add_voucher_code') as string);
      } else {
        setHelperMessage(
          translate('AddVoucher_ChangeVoucherWarning') as string,
        );
        setModalTitle(translate('Change_voucher') as string);
      }
    }
  }, [showVoucherDialog]);

  const hintTextOrError = error || helperMessage;

  const handleSubmit = () => {
    if (
      !menu ||
      !userInput ||
      !orderPayload ||
      typeof orderPayload === 'string'
    ) {
      return;
    }
    setError('');
    setIsLoading(true);
    checkUserVoucher({ orderPayload, code: userInput })
      .then((newVoucher: VoucherBasketItemDm | null) => {
        if (newVoucher) {
          enqueueSnackbar(translate('LoyaltyPopout_voucherAddedMessage'), {
            variant: 'reward',
          });
          trackVoucherAdded(newVoucher);
        } else {
          enqueueSnackbar(translate('Unexpected_error_occurred'), {
            variant: 'error',
          });
        }
        handleClose();
      })
      .catch((voucherError: Error) => {
        setError(voucherError.message);
      })
      .finally(() => {
        setUserInput('');
        setIsLoading(false);
      });
  };

  const DialogActions = (
    <Button
      data-fd="voucher-add"
      disabled={!userInput}
      fullWidth
      onClick={handleSubmit}
      startIcon={!voucher.code && <AddIcon />}
      status={isLoading ? 'loading' : 'default'}
    >
      {modalTitle}
    </Button>
  );

  return (
    <Dialog
      actions={DialogActions}
      dialogName="checkout-voucher"
      onTitleClick={handleClose}
      open={showVoucherDialog}
    >
      <header className="fd-header">
        <Typography className={classes.title} variant="h1">
          {modalTitle}
        </Typography>
      </header>

      <article className="fd-content__main">
        <TextField
          autoFocus
          data-fd="voucher-input"
          disabled={isLoading}
          error={!!error}
          fullWidth
          helperText={hintTextOrError}
          onChange={(e) => setUserInput(e.target.value)}
          placeholder={translate('Enter_your_voucher_code_here').toString()}
          value={userInput}
        />
      </article>
    </Dialog>
  );
}
